import { useQuery } from "@tanstack/react-query";
import { CircleCheckIcon, CircleHelpIcon, CircleXIcon, TriangleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { BarList } from "../charts/bar-list";
import { DistributionStrip } from "../charts/distribution-strip";
import { ChartFrame, type Column, DataTable, type Row } from "../charts/frame";
import { type Series, TimeSeries } from "../charts/time-series";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { type CoreOutputs, reportOf, useTRPC } from "../trpc";
import { useView } from "../view";

type Health = CoreOutputs["health"]["history"];
type HealthPoint = NonNullable<Health["points"][number]>;

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

export function HealthPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { installation, range } = useView();
  const history = useQuery(trpc.health.history.queryOptions({ name: installation, range }));
  const recent = useQuery(trpc.health.recent.queryOptions({ name: installation }));
  const procedures = useQuery(trpc.procedures.queryOptions({ name: installation, range }));
  const detail = useQuery(trpc.installation.get.queryOptions({ name: installation }));
  return (
    <div className="space-y-8">
      <Group title={t("adminApp.health.availability")}>
        <QueryView query={recent}>{(data) => <StatusStrip data={data} />}</QueryView>
        <QueryView query={history}>{(data) => <Latency data={data} />}</QueryView>
      </Group>
      <Group title={t("adminApp.health.traffic")}>
        <QueryView query={history}>{(data) => <Traffic data={data} />}</QueryView>
        <QueryView query={procedures}>{(data) => <Versions data={data} />}</QueryView>
      </Group>
      <Group title={t("adminApp.health.errors")}>
        <div className="grid grid-cols-2 gap-4">
          <QueryView query={history}>{(data) => <Unexpected data={data} />}</QueryView>
          <QueryView query={history}>{(data) => <SlowRequests data={data} />}</QueryView>
        </div>
        <QueryView query={detail}>{(data) => <RecentErrors data={data} />}</QueryView>
        <div className="grid grid-cols-2 gap-4">
          <QueryView query={procedures}>{(data) => <Friction data={data} />}</QueryView>
          <QueryView query={procedures}>{(data) => <SlowAndFailing data={data} />}</QueryView>
        </div>
      </Group>
      <Group title={t("adminApp.health.resources")}>
        <div className="grid grid-cols-2 gap-4">
          <QueryView query={history}>{(data) => <Memory data={data} />}</QueryView>
          <QueryView query={history}>{(data) => <EventLoop data={data} />}</QueryView>
          <QueryView query={detail}>{(data) => <Database data={data} />}</QueryView>
          <QueryView query={detail}>{(data) => <Files data={data} />}</QueryView>
          <QueryView query={detail}>{(data) => <Catalogue data={data} />}</QueryView>
          <MailFigures />
        </div>
      </Group>
    </div>
  );
}

/** A series of one health figure over the periods. */
function points(data: Health, pick: (p: HealthPoint) => number | null): (number | null)[] {
  return data.points.map((p) => (p ? pick(p) : null));
}

function rowsOf(data: Health, columns: Record<string, (p: HealthPoint) => number | null>): Row[] {
  return data.periods.map((period, i) => {
    const p = data.points[i];
    return {
      period,
      ...Object.fromEntries(
        Object.entries(columns).map(([key, pick]) => [key, p ? pick(p) : null]),
      ),
    };
  });
}

/** One figure over the range as a time series in a frame with its table and CSV. */
function HealthSeries({
  data,
  title,
  description,
  csvName,
  series,
  formatValue,
  integer = false,
}: {
  data: Health;
  title: string;
  description?: string;
  csvName: string;
  series: { key: string; label: string; pick: (p: HealthPoint) => number | null }[];
  formatValue: (n: number) => string;
  /** Counts: whole-number ticks. */
  integer?: boolean;
}) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const columns: Column[] = [
    { key: "period", label: t("adminApp.health.period"), format: (v) => format.period(String(v)) },
    ...series.map((s) => ({
      key: s.key,
      label: s.label,
      numeric: true,
      format: (v: unknown) => (typeof v === "number" ? formatValue(v) : "–"),
    })),
  ];
  const lines: Series[] = series.map((s) => ({
    key: s.key,
    label: s.label,
    points: points(data, s.pick),
  }));
  return (
    <ChartFrame
      title={title}
      description={description}
      csvName={csvName}
      columns={columns}
      rows={rowsOf(data, Object.fromEntries(series.map((s) => [s.key, s.pick])))}
    >
      <TimeSeries
        periods={data.periods}
        series={lines}
        formatValue={formatValue}
        formatPeriod={format.period}
        label={title}
        height={180}
        integer={integer}
      />
    </ChartFrame>
  );
}

const STRIP = {
  up: { icon: CircleCheckIcon, cell: "bg-ok", text: "text-ok-fg" },
  partial: { icon: TriangleAlertIcon, cell: "bg-partial", text: "text-partial-fg" },
  down: { icon: CircleXIcon, cell: "bg-missing", text: "text-missing-fg" },
  none: { icon: CircleHelpIcon, cell: "bg-muted", text: "text-muted-foreground" },
} as const;

function stripState(share: number | null | undefined): keyof typeof STRIP {
  if (share === null || share === undefined) return "none";
  if (share >= 1) return "up";
  return share <= 0 ? "down" : "partial";
}

function StatusStrip({ data }: { data: CoreOutputs["health"]["recent"] }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const states = data.points.map((p) => stripState(p?.upShare));
  const count = (state: keyof typeof STRIP) => states.filter((s) => s === state).length;
  const columns: Column[] = [
    { key: "hour", label: t("adminApp.health.hour"), format: (v) => format.period(String(v)) },
    {
      key: "upShare",
      label: t("adminApp.health.upInside"),
      numeric: true,
      format: (v) => format.percent(v as number),
    },
    {
      key: "externalUpShare",
      label: t("adminApp.health.upOutside"),
      numeric: true,
      format: (v) => format.percent(v as number),
    },
    {
      key: "requests",
      label: t("adminApp.health.requests"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
  ];
  const rows = data.hours.map((hour, i) => ({
    hour,
    upShare: data.points[i]?.upShare ?? null,
    externalUpShare: data.points[i]?.externalUpShare ?? null,
    requests: data.points[i]?.requests ?? null,
  }));
  return (
    <ChartFrame
      title={t("adminApp.health.last48Hours")}
      description={t("adminApp.health.last48HoursHint")}
      csvName="availability-48-hours"
      columns={columns}
      rows={rows}
    >
      <div
        role="img"
        aria-label={t("adminApp.health.stripLabel", {
          up: count("up"),
          partial: count("partial"),
          down: count("down"),
          none: count("none"),
        })}
        className="flex h-8 gap-0.5"
      >
        {data.hours.map((hour, i) => (
          <div
            key={hour}
            title={`${format.period(hour)}: ${format.percent(data.points[i]?.upShare)}`}
            className={cn("flex-1 rounded-[3px]", STRIP[states[i] ?? "none"].cell)}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{format.period(data.hours[0] ?? "")}</span>
        <span>{format.period(data.hours.at(-1) ?? "")}</span>
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {(Object.keys(STRIP) as (keyof typeof STRIP)[]).map((state) => {
          const { icon: Icon, text } = STRIP[state];
          return (
            <li key={state} className={cn("flex items-center gap-1.5", text)}>
              <Icon className="size-3.5" aria-hidden />
              {t(`adminApp.health.strip.${state}`, { count: count(state) })}
            </li>
          );
        })}
      </ul>
    </ChartFrame>
  );
}

function Latency({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <HealthSeries
      data={data}
      title={t("adminApp.health.latency")}
      description={t("adminApp.health.latencyHint")}
      csvName="latency"
      formatValue={format.ms}
      series={[
        { key: "p50", label: t("adminApp.health.median"), pick: (p) => p.p50Ms },
        { key: "p95", label: "p95", pick: (p) => p.p95Ms },
      ]}
    />
  );
}

function Traffic({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <div className="grid grid-cols-2 gap-4">
      <HealthSeries
        data={data}
        title={t("adminApp.health.requests")}
        csvName="requests"
        integer
        formatValue={format.compact}
        series={[
          { key: "requests", label: t("adminApp.health.requests"), pick: (p) => p.requests },
        ]}
      />
      <HealthSeries
        data={data}
        title={t("adminApp.health.serverErrors")}
        description={t("adminApp.health.serverErrorsHint")}
        csvName="server-errors"
        formatValue={format.percent}
        series={[
          {
            key: "share",
            label: t("adminApp.health.serverErrors"),
            pick: (p) => p.serverErrorShare,
          },
        ]}
      />
    </div>
  );
}

type Procedures = CoreOutputs["procedures"];

function Versions({ data }: { data: Procedures }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const columns: Column[] = [
    { key: "version", label: t("adminApp.overview.version") },
    {
      key: "calls",
      label: t("adminApp.health.calls"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    {
      key: "unexpected",
      label: t("adminApp.health.unexpected"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    { key: "p95Ms", label: "p95", numeric: true, format: (v) => format.ms(v as number) },
  ];
  const rows = data.versions.map((v) => ({ ...v }));
  return (
    <ChartFrame
      title={t("adminApp.health.perVersion")}
      description={t("adminApp.health.perVersionHint")}
      csvName="versions"
      columns={columns}
      rows={rows}
    >
      {rows.length ? (
        <DataTable columns={columns} rows={rows} />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.health.noCalls")}</p>
      )}
    </ChartFrame>
  );
}

function Unexpected({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <HealthSeries
      data={data}
      title={t("adminApp.health.unexpected")}
      description={t("adminApp.health.unexpectedHint")}
      csvName="unexpected-errors"
      integer
      formatValue={format.count}
      series={[
        { key: "unexpected", label: t("adminApp.health.unexpected"), pick: (p) => p.unexpected },
      ]}
    />
  );
}

type Detail = CoreOutputs["installation"]["get"];

function RecentErrors({ data }: { data: Detail }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const errors = [
    ...((data.status?.recentErrors ?? []) as {
      at: string;
      where: string;
      errorClass: string;
      sqlState: string | null;
    }[]),
  ].reverse();
  const columns: Column[] = [
    { key: "at", label: t("adminApp.health.when"), format: (v) => format.dateTime(v as string) },
    { key: "where", label: t("adminApp.health.where") },
    { key: "errorClass", label: t("adminApp.health.errorClass") },
    { key: "sqlState", label: t("adminApp.health.sqlState") },
  ];
  return (
    <ChartFrame
      title={t("adminApp.health.recentErrors")}
      description={t("adminApp.health.recentErrorsHint")}
      csvName="recent-errors"
      columns={columns}
      rows={errors}
    >
      {errors.length ? (
        <DataTable columns={columns} rows={errors} />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.health.noErrors")}</p>
      )}
    </ChartFrame>
  );
}

function Friction({ data }: { data: Procedures }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const rows = data.procedures
    .map((p) => ({
      path: p.path,
      total: p.appErrors.reduce((sum, e) => sum + e.count, 0),
      codes: p.appErrors,
    }))
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total);
  const columns: Column[] = [
    { key: "path", label: t("adminApp.health.procedure") },
    { key: "code", label: t("adminApp.health.code") },
    {
      key: "count",
      label: t("adminApp.health.count"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
  ];
  const csvRows = rows.flatMap((p) =>
    p.codes.map((c) => ({ path: p.path, code: c.code, count: c.count })),
  );
  return (
    <ChartFrame
      title={t("adminApp.health.friction")}
      description={t("adminApp.health.frictionHint")}
      csvName="error-codes"
      columns={columns}
      rows={csvRows}
    >
      <BarList
        items={rows.slice(0, 10).map((p) => ({
          key: p.path,
          label: <span className="font-mono text-[13px]">{p.path}</span>,
          value: p.total,
          detail: p.codes
            .slice(0, 3)
            .map((c) => `${c.code} ${format.count(c.count)}`)
            .join(" · "),
        }))}
        formatValue={format.count}
        empty={t("adminApp.health.noFriction")}
      />
    </ChartFrame>
  );
}

function SlowAndFailing({ data }: { data: Procedures }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const slow = data.procedures
    .filter((p) => p.p95Ms !== null)
    .sort((a, b) => (b.p95Ms ?? 0) - (a.p95Ms ?? 0))
    .slice(0, 8);
  const failing = data.procedures
    .filter((p) => p.unexpected > 0)
    .sort((a, b) => b.unexpected - a.unexpected)
    .slice(0, 5);
  const columns: Column[] = [
    { key: "path", label: t("adminApp.health.procedure") },
    {
      key: "calls",
      label: t("adminApp.health.calls"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    {
      key: "unexpected",
      label: t("adminApp.health.unexpected"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    { key: "p95Ms", label: "p95", numeric: true, format: (v) => format.ms(v as number) },
  ];
  return (
    <ChartFrame
      title={t("adminApp.health.slowAndFailing")}
      description={t("adminApp.health.slowAndFailingHint")}
      csvName="procedures"
      columns={columns}
      rows={data.procedures.map((p) => ({
        path: p.path,
        calls: p.calls,
        unexpected: p.unexpected,
        p95Ms: p.p95Ms,
      }))}
    >
      <BarList
        items={slow.map((p) => ({
          key: p.path,
          label: <span className="font-mono text-[13px]">{p.path}</span>,
          value: p.p95Ms ?? 0,
        }))}
        formatValue={format.ms}
        empty={t("adminApp.health.noCalls")}
      />
      {failing.length ? (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-medium text-muted-foreground">
            {t("adminApp.health.failing")}
          </h3>
          <BarList
            items={failing.map((p) => ({
              key: p.path,
              label: <span className="font-mono text-[13px]">{p.path}</span>,
              value: p.unexpected,
              detail: t("adminApp.health.ofCalls", { calls: format.count(p.calls) }),
            }))}
            formatValue={format.count}
          />
        </div>
      ) : null}
    </ChartFrame>
  );
}

function SlowRequests({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <HealthSeries
      data={data}
      title={t("adminApp.health.slowRequests")}
      csvName="slow-requests"
      integer
      formatValue={format.count}
      series={[
        { key: "slow", label: t("adminApp.health.slowRequests"), pick: (p) => p.slowRequests },
      ]}
    />
  );
}

function Memory({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <HealthSeries
      data={data}
      title={t("adminApp.health.memory")}
      description={t("adminApp.health.memoryHint")}
      csvName="memory"
      formatValue={format.bytes}
      series={[
        { key: "peak", label: t("adminApp.health.peakMemory"), pick: (p) => p.peakMemoryBytes },
        { key: "limit", label: t("adminApp.health.memoryLimit"), pick: (p) => p.memoryLimitBytes },
      ]}
    />
  );
}

function EventLoop({ data }: { data: Health }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  return (
    <HealthSeries
      data={data}
      title={t("adminApp.health.eventLoop")}
      description={t("adminApp.health.eventLoopHint")}
      csvName="event-loop"
      formatValue={format.ms}
      series={[
        { key: "peak", label: t("adminApp.health.eventLoop"), pick: (p) => p.peakEventLoopMs },
      ]}
    />
  );
}

function Database({ data }: { data: Detail }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const storage = reportOf(data)?.storage;
  const tables = storage?.largestTables ?? [];
  const columns: Column[] = [
    { key: "name", label: t("adminApp.health.table") },
    {
      key: "bytes",
      label: t("adminApp.health.size"),
      numeric: true,
      format: (v) => format.bytes(v as number),
    },
    {
      key: "rows",
      label: t("adminApp.health.rows"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
  ];
  return (
    <ChartFrame
      title={t("adminApp.health.database")}
      description={t("adminApp.health.databaseHint", {
        size: format.bytes(storage?.databaseBytes),
        connections: format.count(storage?.connections),
      })}
      csvName="database-tables"
      columns={columns}
      rows={tables.map((table) => ({
        name: table.name,
        bytes: table.bytes,
        rows: table.rows ?? null,
      }))}
    >
      <BarList
        items={tables.map((table) => ({
          key: table.name,
          label: <span className="font-mono text-[13px]">{table.name}</span>,
          value: table.bytes,
        }))}
        formatValue={format.bytes}
        empty={t("adminApp.health.noTables")}
      />
    </ChartFrame>
  );
}

function Files({ data }: { data: Detail }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const report = reportOf(data);
  const files = report?.storage?.fileBytes;
  const top = [...(report?.garageTable ?? [])]
    .filter((g) => (g.fileBytes ?? 0) > 0)
    .sort((a, b) => (b.fileBytes ?? 0) - (a.fileBytes ?? 0))
    .slice(0, 10);
  const columns: Column[] = [
    { key: "garage", label: t("adminApp.health.garage") },
    {
      key: "bytes",
      label: t("adminApp.health.size"),
      numeric: true,
      format: (v) => format.bytes(v as number),
    },
  ];
  return (
    <ChartFrame
      title={t("adminApp.health.files")}
      description={t("adminApp.health.filesHint", {
        total: format.bytes(files?.total),
        deduped: format.bytes(files?.deduped),
      })}
      csvName="files-per-garage"
      columns={columns}
      rows={top.map((g) => ({ garage: g.name ?? g.id, bytes: g.fileBytes ?? 0 }))}
    >
      {files?.perGarage ? (
        <div className="mb-4">
          <DistributionStrip distribution={files.perGarage} format={format.bytes} />
        </div>
      ) : null}
      <BarList
        items={top.map((g) => ({ key: g.id, label: g.name ?? g.id, value: g.fileBytes ?? 0 }))}
        formatValue={format.bytes}
        empty={t("adminApp.health.noFiles")}
      />
    </ChartFrame>
  );
}

function Catalogue({ data }: { data: Detail }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const catalogue = reportOf(data)?.catalogue;
  const columns: Column[] = [
    {
      key: "parts",
      label: t("adminApp.health.catalogueParts"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    {
      key: "lastImportAt",
      label: t("adminApp.health.lastImport"),
      format: (v) => format.dateTime(v as string),
    },
  ];
  const rows = [{ parts: catalogue?.parts ?? null, lastImportAt: catalogue?.lastImportAt ?? null }];
  return (
    <ChartFrame
      title={t("adminApp.health.catalogue")}
      csvName="catalogue"
      columns={columns}
      rows={rows}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t("adminApp.health.catalogueParts")}</dt>
        <dd className="tabular">{format.count(catalogue?.parts)}</dd>
        <dt className="text-muted-foreground">{t("adminApp.health.lastImport")}</dt>
        <dd>
          {catalogue?.lastImportAt
            ? format.dateTime(catalogue.lastImportAt)
            : t("adminApp.server.never")}
        </dd>
      </dl>
    </ChartFrame>
  );
}

/** Mail sent and failed per kind over the range (signals mail.sent and mail.failed). */
function MailFigures() {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const trpc = useTRPC();
  const { installation, range } = useView();
  const name = installation;
  const sent = useQuery(trpc.signals.history.queryOptions({ name, range, signal: "mail.sent" }));
  const failed = useQuery(
    trpc.signals.history.queryOptions({ name, range, signal: "mail.failed" }),
  );
  const kinds = [
    ...new Set([...(sent.data?.values ?? []), ...(failed.data?.values ?? [])].map((v) => v.value)),
  ];
  const total = (list: typeof sent.data, kind: string) =>
    list?.values.find((v) => v.value === kind)?.total ?? 0;
  const columns: Column[] = [
    { key: "kind", label: t("adminApp.health.mailKind") },
    {
      key: "sent",
      label: t("adminApp.health.sent"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
    {
      key: "failed",
      label: t("adminApp.health.failed"),
      numeric: true,
      format: (v) => format.count(v as number),
    },
  ];
  const rows = kinds.map((kind) => ({
    kind,
    sent: total(sent.data, kind),
    failed: total(failed.data, kind),
  }));
  return (
    <QueryView query={sent}>
      {() => (
        <ChartFrame
          title={t("adminApp.health.mail")}
          description={t("adminApp.health.mailHint")}
          csvName="mail"
          columns={columns}
          rows={rows}
        >
          <BarList
            items={rows.map((row) => ({
              key: row.kind,
              label: t(`adminApp.health.mailKinds.${row.kind as "invitation"}`, {
                defaultValue: row.kind,
              }),
              value: row.sent,
              detail: row.failed
                ? t("adminApp.health.mailFailed", { count: row.failed })
                : undefined,
            }))}
            formatValue={format.count}
            empty={t("adminApp.health.noMail")}
          />
        </ChartFrame>
      )}
    </QueryView>
  );
}
